import AccountCircle from "@mui/icons-material/AccountCircle";
import Add from "@mui/icons-material/Add";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Circle from "@mui/icons-material/Circle";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Edit from "@mui/icons-material/Edit";
import Error from "@mui/icons-material/Error";
import Info from "@mui/icons-material/Info";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import Star from "@mui/icons-material/Star";
import { Box, IconButton, LinearProgress, Paper, Stack, Tooltip, Typography } from "@mui/material";
import {
  DataGridPremiumProps,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  gridDetailPanelExpandedRowIdsSelector,
  GridRowId,
  GridRowParams,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import {
  ActivityType,
  FacilityPilotProgramReadyStatus,
  FacilityPilotProgramStatus,
} from "generated-graphql/graphql";
import { LinearIssueStatusMap } from "hooks/useLinearIssueStatuses";
import React, { useCallback, useMemo } from "react";

import { DetailPanelContent } from "./DetailPanelContent";
import {
  getStatusIconWithoutStar,
  getStatusLabel,
  StateRow,
  StatusFilter,
} from "./pilotProgramUtils";
import { PilotWorkflowTagsCell } from "./PilotWorkflowTagsCell";

// Props interface
interface FacilityPilotProgramStatesProps {
  allStateRows: StateRow[];
  loading: boolean;
  statusFilter: StatusFilter;
  linearStatusMap: LinearIssueStatusMap;
  onStatusFilterClick: (filter: StatusFilter) => void;
  onAddFacility: (row: StateRow) => void;
  onEditProgram: (row: StateRow) => void;
  onOpenTagsDialog: (row: StateRow) => void;
  onRemoveFacility: (configId: string) => void;
  removeLoading: boolean;
  yearPicker: React.ReactNode;
}

// Expand/Collapse All Header Component
const ExpandCollapseAllHeader = () => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(apiRef, gridDetailPanelExpandedRowIdsSelector);
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);

  const expandableRowIds = useMemo(() => {
    return Object.keys(rowsLookup).filter((rowId) => {
      const row = rowsLookup[rowId] as StateRow;
      return row.pilotFacilities && row.pilotFacilities.length > 0;
    });
  }, [rowsLookup]);

  const allExpanded =
    expandableRowIds.length > 0 && expandableRowIds.every((id) => expandedRowIds.has(id));

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      expandedRowIds.forEach((rowId: GridRowId) => {
        apiRef.current.toggleDetailPanel(rowId);
      });
    } else {
      expandableRowIds.forEach((rowId) => {
        if (!expandedRowIds.has(rowId)) {
          apiRef.current.toggleDetailPanel(rowId);
        }
      });
    }
  };

  return (
    <IconButton
      onClick={handleExpandCollapseAll}
      size="small"
      title={allExpanded ? "Collapse all" : "Expand all"}
    >
      {allExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

// Expand/Collapse Cell Component
const ExpandCollapseCell = ({ row, id }: { row: StateRow; id: GridRowId }) => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(apiRef, gridDetailPanelExpandedRowIdsSelector);
  const isExpanded = expandedRowIds.has(id);
  const hasPilotFacilities = row.pilotFacilities && row.pilotFacilities.length > 0;

  if (!hasPilotFacilities) {
    return null;
  }

  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        apiRef.current.toggleDetailPanel(id);
      }}
    >
      {isExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

export function FacilityPilotProgramStates({
  allStateRows,
  loading,
  statusFilter,
  linearStatusMap,
  onStatusFilterClick,
  onAddFacility,
  onEditProgram,
  onOpenTagsDialog,
  onRemoveFacility,
  removeLoading,
  yearPicker,
}: FacilityPilotProgramStatesProps) {
  const apiRef = useGridApiRef();

  // Define columns for states view
  const stateColumns: GridColDef<StateRow>[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderHeader: () => <ExpandCollapseAllHeader />,
        renderCell: ({ row, id }) => <ExpandCollapseCell row={row} id={id} />,
      },
      {
        field: "statusIcon",
        headerName: "State Enabled",
        width: 80,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            State Enabled
          </Typography>
        ),
        renderCell: ({ row }) => {
          if (row.isStateEnabled) {
            return (
              <Tooltip title="State is enabled">
                <Star fontSize="small" sx={{ color: "#FFD700", opacity: 0.9 }} />
              </Tooltip>
            );
          }
          return null;
        },
      },
      {
        field: "checklist",
        headerName: "Checklist",
        width: 90,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          return (
            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
              {/* Left dot: Pilot Status */}
              <Tooltip title={getStatusLabel(row.status)}>
                {row.readyStatus === FacilityPilotProgramReadyStatus.Ready ? (
                  <CheckCircle fontSize="small" sx={{ color: "success.main", opacity: 0.8 }} />
                ) : (
                  getStatusIconWithoutStar(row.status)
                )}
              </Tooltip>

              {/* Middle dot: QA Complete */}
              <Tooltip title={row.qaComplete ? "QA is complete" : "QA is not complete"}>
                {row.qaComplete ? (
                  <CheckCircle fontSize="small" sx={{ color: "success.main", opacity: 0.8 }} />
                ) : (
                  <Circle fontSize="small" sx={{ color: "text.disabled", opacity: 0.6 }} />
                )}
              </Tooltip>

              {/* Right dot: Documentation Complete */}
              <Tooltip
                title={
                  row.documentationComplete
                    ? "Documentation is complete"
                    : "Documentation is not complete"
                }
              >
                {row.documentationComplete ? (
                  <CheckCircle fontSize="small" sx={{ color: "success.main", opacity: 0.8 }} />
                ) : (
                  <Circle fontSize="small" sx={{ color: "text.disabled", opacity: 0.6 }} />
                )}
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        field: "state",
        headerName: "State/Territory",
        width: 60,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="bold">
            {row.state}
          </Typography>
        ),
      },
      {
        field: "stateName",
        headerName: "State Name",
        width: 110,
      },
      {
        field: "portalTech",
        headerName: "Portal Tech",
        width: 100,
        valueGetter: (_, row) => row.portalTech ?? "-",
      },
      {
        field: "totalReportingFacilities",
        headerName: "Reporting Facilities",
        width: 105,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Reporting Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="body2">{row.totalReportingFacilities}</Typography>
        ),
      },
      {
        field: "verifiedReportCount",
        headerName: "Candidate Facilities",
        width: 90,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Candidate Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Tooltip title="Reports with encampStatus = PROCESSING">
            <Typography variant="body2">{row.verifiedReportCount}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "pilotFacilityCount",
        headerName: "Pilot Facilities",
        width: 80,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Pilot Facilities
          </Typography>
        ),
        renderCell: ({ row }) => {
          const hasStaffActivity = row.pilotFacilities.some(
            (f) => f.currentActivityType === ActivityType.Staff
          );
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {hasStaffActivity && (
                <AccountCircle sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
              )}
              <Typography
                variant="body2"
                fontWeight={row.pilotFacilityCount > 0 ? "bold" : "normal"}
              >
                {row.pilotFacilityCount}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "tags",
        headerName: "Pilot Workflow Tags",
        width: 270,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <PilotWorkflowTagsCell
              row={row}
              onOpenTagsDialog={onOpenTagsDialog}
              linearStatusMap={linearStatusMap}
            />
          );
        },
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 150,
        sortable: false,
        cellClassName: "notes-cell",
        renderCell: ({ row }) => (
          <Tooltip title={row.notes || ""}>
            <Box
              sx={{
                width: "100%",
                fontSize: "0.7rem",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
                wordWrap: "break-word",
                lineHeight: 1.2,
              }}
            >
              {row.notes || ""}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const hasNoCandidateReports = (row.verifiedReportCount ?? 0) === 0;
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {hasNoCandidateReports ? (
                <Tooltip title="No candidate facilities">
                  <span>
                    <IconButton
                      size="small"
                      disabled
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                        cursor: "not-allowed",
                        ".MuiDataGrid-row:hover &": {
                          opacity: 1,
                        },
                      }}
                    >
                      <DoNotDisturb />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Add pilot facility">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddFacility(row);
                    }}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      ".MuiDataGrid-row:hover &": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit pilot program">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditProgram(row);
                  }}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.2s",
                    ".MuiDataGrid-row:hover &": {
                      opacity: 1,
                    },
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [onOpenTagsDialog, onAddFacility, onEditProgram, linearStatusMap]
  );

  // Filter state rows: exclude states with 0 reporting facilities and apply status filter
  const stateRows = useMemo(() => {
    let filtered = allStateRows.filter((row) => row.totalReportingFacilities > 0);

    // Apply status filter if active
    if (statusFilter !== null) {
      if (statusFilter === "enabled") {
        filtered = filtered.filter((row) => row.isStateEnabled);
      } else if (statusFilter === "notEnabled") {
        filtered = filtered.filter((row) => !row.isStateEnabled);
      } else if (statusFilter === "started") {
        filtered = filtered.filter((row) => row.status !== FacilityPilotProgramStatus.NotStarted);
      } else {
        filtered = filtered.filter((row) => row.status === statusFilter);
      }
    }

    return filtered;
  }, [allStateRows, statusFilter]);

  // Calculate summary statistics for states
  const stateSummaryStats = useMemo(() => {
    const filteredRows = allStateRows.filter((row) => row.totalReportingFacilities > 0);

    if (filteredRows.length === 0) {
      return {
        totalStates: 0,
        statesEnabled: 0,
        statesEnabledPercent: 0,
        statesStarted: 0,
        statesStartedPercent: 0,
        statesCompleted: 0,
        statesCompletedPercent: 0,
        statesInError: 0,
        statesInErrorPercent: 0,
        statesNotStarted: 0,
        statesNotStartedPercent: 0,
        totalPilotFacilities: 0,
        totalVerifiedReports: 0,
        pilotFacilitiesWaitingForStaff: 0,
        readyToStartStates: 0,
        statesWithNoCandidates: 0,
        statesCanBeEnabled: 0,
      };
    }

    const totalStates = filteredRows.length;
    const statesEnabled = filteredRows.filter((r) => r.isStateEnabled).length;
    const statesStarted = filteredRows.filter(
      (r) => r.status !== FacilityPilotProgramStatus.NotStarted
    ).length;
    const statesCompleted = filteredRows.filter(
      (r) => r.status === FacilityPilotProgramStatus.Completed
    ).length;
    const statesInError = filteredRows.filter(
      (r) => r.status === FacilityPilotProgramStatus.InError
    ).length;
    const statesNotStarted = filteredRows.filter(
      (r) => r.status === FacilityPilotProgramStatus.NotStarted
    ).length;
    const totalPilotFacilities = filteredRows.reduce((acc, r) => acc + r.pilotFacilityCount, 0);
    const totalVerifiedReports = filteredRows.reduce((acc, r) => acc + r.verifiedReportCount, 0);

    const pilotFacilitiesWaitingForStaff = filteredRows.reduce((acc, r) => {
      return (
        acc + r.pilotFacilities.filter((pf) => pf.currentActivityType === ActivityType.Staff).length
      );
    }, 0);

    const readyToStartStates = filteredRows.filter(
      (r) => r.status === FacilityPilotProgramStatus.NotStarted && (r.verifiedReportCount ?? 0) > 0
    ).length;

    const statesWithNoCandidates = filteredRows.filter(
      (r) => (r.verifiedReportCount ?? 0) === 0
    ).length;

    const statesCanBeEnabled = filteredRows.filter(
      (r) =>
        !r.isStateEnabled &&
        (r.status === FacilityPilotProgramStatus.Completed ||
          r.readyStatus === FacilityPilotProgramReadyStatus.Ready) &&
        r.documentationComplete === true &&
        r.qaComplete === true
    ).length;

    return {
      totalStates,
      statesEnabled,
      statesEnabledPercent: Math.round((statesEnabled / totalStates) * 100),
      statesStarted,
      statesStartedPercent: Math.round((statesStarted / totalStates) * 100),
      statesCompleted,
      statesCompletedPercent: Math.round((statesCompleted / totalStates) * 100),
      statesInError,
      statesInErrorPercent: Math.round((statesInError / totalStates) * 100),
      statesNotStarted,
      statesNotStartedPercent: Math.round((statesNotStarted / totalStates) * 100),
      totalPilotFacilities,
      totalVerifiedReports,
      pilotFacilitiesWaitingForStaff,
      readyToStartStates,
      statesWithNoCandidates,
      statesCanBeEnabled,
    };
  }, [allStateRows]);

  const getDetailPanelHeight = useCallback<
    NonNullable<DataGridPremiumProps["getDetailPanelHeight"]>
  >(() => "auto", []);

  const handleStateRowClick = useCallback(
    (params: GridRowParams<StateRow>) => {
      if (params.row.pilotFacilities && params.row.pilotFacilities.length > 0) {
        apiRef.current?.toggleDetailPanel(params.id);
      }
    },
    [apiRef]
  );

  const getStateDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps<StateRow>["getDetailPanelContent"]>
  >(
    ({ row }) => {
      if (!row.pilotFacilities || row.pilotFacilities.length === 0) {
        return null;
      }
      return (
        <DetailPanelContent
          pilotFacilities={row.pilotFacilities}
          onRemove={onRemoveFacility}
          removeLoading={removeLoading}
          linearStatusMap={linearStatusMap}
        />
      );
    },
    [onRemoveFacility, removeLoading, linearStatusMap]
  );

  return (
    <>
      {/* Header: Year Picker + Summary Stats */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap={1}
        sx={{ mb: 2 }}
      >
        {yearPicker}

        {/* Summary Stats */}
        <Stack direction="row" spacing={2} alignItems="stretch" flexWrap="wrap" rowGap={1}>
          {/* States Enabled mini-stat */}
          <Tooltip
            title={
              statusFilter === "enabled"
                ? "Showing only enabled states"
                : statusFilter === "notEnabled"
                  ? "Showing only not enabled states"
                  : "Click to filter: Show enabled → Show not enabled → Show all"
            }
          >
            <Paper
              variant="outlined"
              onClick={() => onStatusFilterClick("enabled")}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor:
                  statusFilter === "enabled" || statusFilter === "notEnabled"
                    ? "#FFD700"
                    : "transparent",
                bgcolor:
                  statusFilter === "enabled" || statusFilter === "notEnabled"
                    ? "action.hover"
                    : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Star fontSize="small" sx={{ color: "#FFD700", opacity: 0.9 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {statusFilter === "enabled"
                    ? "States Enabled"
                    : statusFilter === "notEnabled"
                      ? "States Not Enabled"
                      : "States Enabled"}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#FFD700" }}>
                  {stateSummaryStats.statesEnabled}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {stateSummaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={stateSummaryStats.statesEnabledPercent}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#FFD700",
                  },
                }}
              />
            </Paper>
          </Tooltip>

          {/* States Started mini-stat */}
          <Tooltip title="States that are not in 'Not Started' status">
            <Paper
              variant="outlined"
              onClick={() => onStatusFilterClick("started")}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor: statusFilter === "started" ? "info.main" : "transparent",
                bgcolor: statusFilter === "started" ? "action.hover" : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Info fontSize="small" sx={{ color: "info.light", opacity: 0.8 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Started
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  {stateSummaryStats.statesStarted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {stateSummaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={stateSummaryStats.statesStartedPercent}
                color="info"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States In Error mini-stat */}
          <Tooltip title="States with pilot facilities in error state">
            <Paper
              variant="outlined"
              onClick={() => onStatusFilterClick(FacilityPilotProgramStatus.InError)}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor:
                  statusFilter === FacilityPilotProgramStatus.InError
                    ? "error.main"
                    : "transparent",
                bgcolor:
                  statusFilter === FacilityPilotProgramStatus.InError
                    ? "action.hover"
                    : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Error fontSize="small" sx={{ color: "error.light", opacity: 0.8 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  In Error
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {stateSummaryStats.statesInError}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {stateSummaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={stateSummaryStats.statesInErrorPercent}
                color="error"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States Completed mini-stat */}
          <Tooltip title="States where at least one pilot facility has a completed workflow">
            <Paper
              variant="outlined"
              onClick={() => onStatusFilterClick(FacilityPilotProgramStatus.Completed)}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor:
                  statusFilter === FacilityPilotProgramStatus.Completed
                    ? "success.main"
                    : "transparent",
                bgcolor:
                  statusFilter === FacilityPilotProgramStatus.Completed
                    ? "action.hover"
                    : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CheckCircle fontSize="small" sx={{ color: "success.light", opacity: 0.8 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Completed
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {stateSummaryStats.statesCompleted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {stateSummaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={stateSummaryStats.statesCompletedPercent}
                color="success"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States Not Started mini-stat */}
          <Tooltip title="States that have not started the pilot program">
            <Paper
              variant="outlined"
              onClick={() => onStatusFilterClick(FacilityPilotProgramStatus.NotStarted)}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor:
                  statusFilter === FacilityPilotProgramStatus.NotStarted
                    ? "text.disabled"
                    : "transparent",
                bgcolor:
                  statusFilter === FacilityPilotProgramStatus.NotStarted
                    ? "action.hover"
                    : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Circle fontSize="small" sx={{ color: "text.disabled", opacity: 0.6 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Not Started
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="text.secondary">
                  {stateSummaryStats.statesNotStarted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {stateSummaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={stateSummaryStats.statesNotStartedPercent}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "text.disabled",
                  },
                }}
              />
            </Paper>
          </Tooltip>

          {/* Totals summary */}
          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 0.75,
              minWidth: 140,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 0.25,
              bgcolor: "action.hover",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {stateSummaryStats.pilotFacilitiesWaitingForStaff}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                pilot facilities waiting for Staff
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {stateSummaryStats.readyToStartStates}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                states can be started
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {stateSummaryStats.statesCanBeEnabled}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                states can be enabled
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      {/* Data Grid */}
      <Box sx={{ height: "calc(100vh - 320px)", minHeight: 400, width: "100%", mt: 2 }}>
        <DataGrid<StateRow>
          apiRef={apiRef}
          rows={stateRows}
          columns={stateColumns}
          loading={loading}
          getRowId={(row) => row.state}
          getDetailPanelContent={getStateDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          onRowClick={handleStateRowClick}
          disableRowGrouping={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "totalReportingFacilities", sort: "desc" }],
            },
          }}
          pageSizeOptions={[25, 51, 100]}
          sx={{
            "& .MuiDataGrid-detailPanel": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-row.Mui-expanded": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-cell.notes-cell": {
              whiteSpace: "normal",
              alignItems: "center",
            },
          }}
        />
      </Box>
    </>
  );
}
